import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { emergencySos } from '../lib/api';
import { useAuth } from '../lib/auth';
import { Siren, PhoneCall, CheckCircle2, Clock, XCircle, MapPin } from 'lucide-react';
import { toast } from 'sonner';

const STATUS_STYLE = {
  open:      { label: 'Open',      cls: 'bg-[#FFF3E0] text-[#B26900] border-[#FFCC80]', Icon: Clock },
  fulfilled: { label: 'Fulfilled', cls: 'bg-[#E8F5E9] text-[#1B5E20] border-[#A5D6A7]', Icon: CheckCircle2 },
  closed:    { label: 'Closed',    cls: 'bg-[#ECEFF1] text-[#37474F] border-[#B0BEC5]', Icon: XCircle },
  expired:   { label: 'Expired',   cls: 'bg-[#ECEFF1] text-[#455A64] border-[#B0BEC5]', Icon: XCircle },
};

export default function EmergencyHistory() {
  const { user } = useAuth();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!user) return;
    setLoading(true);
    emergencySos.mine().then((d) => setRows(d.broadcasts || [])).finally(() => setLoading(false));
  }, [user]);

  const close = async (id) => {
    try {
      await emergencySos.close(id);
      toast.success('SOS closed');
      setRows((rs) => rs.map(r => r.id === id ? { ...r, status: 'closed' } : r));
    } catch { toast.error('Could not close'); }
  };

  if (!user) return (
    <div className="max-w-3xl mx-auto px-6 py-16 text-center">
      <Siren className="w-10 h-10 text-[#B71C1C] mx-auto mb-2"/>
      <div className="font-heading font-black text-2xl">Sign in to see your emergencies</div>
      <p className="text-sm text-ink/60 mt-2">Your SOS history is private — sign in to view responses from pharmacies.</p>
    </div>
  );

  return (
    <div className="max-w-3xl mx-auto px-4 md:px-6 py-8" data-testid="emergency-history-page">
      <div className="inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.2em] font-black text-[#B71C1C]">
        <Siren className="w-4 h-4"/>My emergencies
      </div>
      <h1 className="font-heading font-black text-3xl md:text-4xl mt-1">Emergency medicine history</h1>
      <p className="text-sm text-ink/60 mt-1">Every SOS you broadcast and what the pharmacies said.</p>

      {loading ? (
        <div className="mt-8 text-ink/50">Loading…</div>
      ) : rows.length === 0 ? (
        <div className="mt-8 rounded-2xl border-2 border-dashed border-ink/20 p-8 text-center">
          <div className="font-heading font-bold text-lg">No emergencies yet — good news.</div>
          <p className="text-sm text-ink/60 mt-1">Tap the red SOS button whenever you need a medicine urgently.</p>
        </div>
      ) : (
        <ul className="mt-6 space-y-3">
          {rows.map((b) => {
            const st = STATUS_STYLE[b.status] || STATUS_STYLE.open;
            const responded = (b.responses || []).length;
            const haveIt = (b.responses || []).filter(r => r.has_it);
            return (
              <li
                key={b.id}
                data-testid={`emergency-item-${b.id}`}
                className="rounded-2xl border-2 border-ink/10 bg-white p-4"
              >
                <div className="flex items-start gap-3 flex-wrap">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <div className="font-heading font-black text-lg">{b.medicine_name}</div>
                      <span className={`inline-flex items-center gap-1 rounded-full border px-2.5 py-0.5 text-[10px] font-black uppercase tracking-wider ${st.cls}`}>
                        <st.Icon className="w-3 h-3"/>{st.label}
                      </span>
                      {b.requires_prescription && (
                        <span className="inline-flex items-center rounded-full bg-[#FFEBEE] text-[#B71C1C] border border-[#EF9A9A] px-2.5 py-0.5 text-[10px] font-black uppercase tracking-wider">
                          Rx-only
                        </span>
                      )}
                    </div>
                    <div className="mt-1 text-xs text-ink/60">
                      Broadcast to <b>{(b.targets || []).length}</b> pharmacies within <b>{b.radius_km} km</b> · {new Date(b.created_at).toLocaleString()}
                    </div>
                    {b.notes && <div className="mt-1 text-sm text-ink/80 italic">&ldquo;{b.notes}&rdquo;</div>}
                  </div>
                  {b.status === 'open' && (
                    <button
                      data-testid={`emergency-close-${b.id}`}
                      onClick={() => close(b.id)}
                      className="text-xs font-black uppercase tracking-wider text-ink/50 hover:text-ink border-2 border-ink/20 hover:border-ink rounded-lg px-3 h-8"
                    >
                      Mark resolved
                    </button>
                  )}
                </div>

                {responded > 0 && (
                  <div className="mt-3 rounded-lg bg-[#F8F9FA] border border-ink/10 p-3">
                    <div className="text-[10px] uppercase tracking-[0.18em] font-black text-[#1B5E20] mb-2">
                      {haveIt.length}/{responded} pharmacies have this medicine
                    </div>
                    <ul className="space-y-2">
                      {b.responses.map((r) => (
                        <li key={r.id} className="flex items-center gap-3 text-sm">
                          <div className="w-8 h-8 rounded-lg bg-white border grid place-items-center shrink-0">
                            <MapPin className="w-4 h-4 text-ink/50"/>
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="font-bold text-ink truncate">
                              {r.store_name}
                              <span className="ml-2 text-[11px] font-mono text-[#B71C1C]">{r.distance_km} km</span>
                            </div>
                            <div className="text-xs text-ink/60 truncate">
                              {r.has_it ? '✓ Has it in stock' : '× Currently out'} · {r.note || 'No note'}
                              {r.price != null && <> · ₹{r.price}</>}
                            </div>
                          </div>
                          {r.has_it && r.phone && (
                            <a
                              data-testid={`emergency-call-${r.id}`}
                              href={`tel:${r.phone}`}
                              className="inline-flex items-center gap-1 rounded-full bg-[#1B5E20] hover:bg-[#0D3F12] text-white px-3 h-8 text-[11px] font-black uppercase tracking-wider transition"
                            >
                              <PhoneCall className="w-3 h-3"/>Call {r.phone}
                            </a>
                          )}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </li>
            );
          })}
        </ul>
      )}

      <div className="mt-8 text-center">
        <Link
          data-testid="emergency-history-home"
          to="/"
          className="text-[11px] uppercase tracking-[0.18em] font-black text-ink/60 hover:text-ink"
        >
          ← Back to home
        </Link>
      </div>
    </div>
  );
}
